'use client';

import { Icon } from '@/app/components/icons';
import { IconChevronLeft } from '@/app/components/icons/ui-actions/icon-chevron-left';
import { IconChevronRight } from '@/app/components/icons/ui-actions/icon-chevron-right';
import type {
  BasePaginationProps,
  PaginationDataKeys,
} from '@/app/components/navigation/pagination/types/pagination-type';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

const VISIBLE_PAGES = 5;

const baseButtonSizeClassName =
  'p-0 relative inline-flex items-center justify-center outline-none';

const BasePaginationControlStyled = cnva([baseButtonSizeClassName], {
  variants: {
    show: {
      true: 'transition-color cursor-pointer hover:text-red-500 focus-visible:text-red-500',
      false: 'invisible',
    },
    size: {
      default: 'size-8 min-w-8 xl:size-10 xl:min-w-10',
      fixed: 'size-8 min-w-8',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const PaginationButtonStyled = cnva(
  [
    baseButtonSizeClassName,
    'rounded-2xs font-noto-bold text-body-1-fixed p-2 text-gray-700',
  ],
  {
    variants: {
      theme: {
        light: '',
        dark: '',
      },
      isActive: {
        true: 'pointer-events-none',
        false: 'cursor-pointer hover:bg-gray-50 focus-visible:bg-gray-50',
      },
      size: {
        default: 'size-8 min-w-8 xl:size-10 xl:min-w-10',
        fixed: 'size-8 min-w-8',
      },
    },
    compoundVariants: [
      {
        theme: 'light',
        isActive: true,
        className: 'bg-red-500 text-white',
      },
      {
        theme: 'dark',
        isActive: true,
        className: 'bg-gray-900 text-white',
      },
    ],
    defaultVariants: {
      theme: 'light',
      size: 'default',
    },
  }
);

const calculateStartPage = (currentPage: number, totalPages: number) => {
  return Math.max(
    1,
    Math.min(
      currentPage - Math.floor(VISIBLE_PAGES / 2),
      totalPages - VISIBLE_PAGES + 1
    )
  );
};

export interface PaginationNumberProps extends BasePaginationProps {
  /**
   * The class name for the control buttons.
   */
  controlClassName?: string;
  /**
   * The size of the pagination buttons.
   */
  size?: 'default' | 'fixed';

  /**
   * When true, renders every page button (1 … totalPages). When false, only a
   * sliding window of up to five page buttons is shown.
   */
  expandable?: boolean;

  /**
   * When true, shows previous/next controls.
   */
  showNavigation?: boolean;

  /**
   * Total number of items across all pages (sets `data-items` when provided).
   */
  totalItems?: number;
}

export const PaginationNumber = ({
  wrapperClassName,
  controlClassName,
  className,
  theme = 'light',
  size = 'default',
  expandable = false,
  showNavigation = true,
  currentPage = 1,
  totalPages = 1,
  totalItems,
  onPageChange,
  ...props
}: PaginationNumberProps) => {
  const handlePageChange = (page: number) => {
    if (page === currentPage) return;
    safeCall(onPageChange, page);
  };

  const showNextControl = expandable
    ? currentPage < totalPages
    : totalPages > VISIBLE_PAGES && currentPage < totalPages;

  const renderPagination = () => {
    if (expandable) {
      return Array.from({ length: totalPages }, (_, index) => {
        const page = index + 1;
        const isActive = page === currentPage;
        return (
          <button
            key={page}
            className={PaginationButtonStyled({
              theme,
              isActive,
              size,
              className,
            })}
            onClick={() => handlePageChange(page)}
            aria-current={isActive ? 'page' : undefined}
            aria-label={`Page ${page}`}
            {...props}
            {...createDataAttributes<PaginationDataKeys>({
              test: `pagination-${page}`,
              active: isActive ? 'true' : null,
            })}
          >
            {page}
          </button>
        );
      });
    }

    const startPage = calculateStartPage(currentPage, totalPages);
    const buttonCount = Math.min(VISIBLE_PAGES, totalPages);

    return Array.from({ length: buttonCount }, (_, index) => {
      const page = startPage + index;
      const isActive = page === currentPage;
      return (
        <button
          key={page}
          className={PaginationButtonStyled({
            theme,
            isActive,
            className,
          })}
          onClick={() => handlePageChange(page)}
          aria-current={isActive ? 'page' : undefined}
          aria-label={`Page ${page}`}
          {...props}
          {...createDataAttributes<PaginationDataKeys>({
            test: `pagination-${page}`,
            active: isActive ? 'true' : null,
          })}
        >
          {page}
        </button>
      );
    });
  };

  return (
    <nav
      className={cn('flex items-center gap-x-3 xl:gap-x-4', wrapperClassName)}
      aria-labelledby="pagination"
      {...createDataAttributes({
        test: 'pagination-number',
      })}
      data-page-total={totalPages}
      {...(totalItems !== undefined ? { 'data-item': totalItems } : {})}
    >
      {!!showNavigation && (
        <button
          title="Previous"
          aria-label="Previous"
          className={cn(
            BasePaginationControlStyled({ show: currentPage > 1, size }),
            controlClassName
          )}
          onClick={() => handlePageChange(currentPage - 1)}
          {...createDataAttributes({
            test: `pagination-prev`,
          })}
        >
          <Icon icon={<IconChevronLeft />} />
        </button>
      )}

      {renderPagination()}

      {!!showNavigation && (
        <button
          title="Next"
          aria-label="Next"
          className={cn(
            BasePaginationControlStyled({
              show: showNextControl,
              size,
            }),
            controlClassName
          )}
          onClick={() => handlePageChange(currentPage + 1)}
          {...createDataAttributes({
            test: `pagination-next`,
          })}
        >
          <Icon icon={<IconChevronRight />} />
        </button>
      )}
    </nav>
  );
};
